import { firstTouchProperties } from "@reactive-resume/utils";
import { useEffect } from "react";

import {
  getFirstTouch,
  getHandedOffDevice,
  handoffTarget,
  installHandoffDecorator,
} from "@/client/analytics/attribution";
import { ga4SetUser } from "@/client/analytics/ga4";
import {
  getDeviceId,
  identifyUser,
  initMixpanel,
  linkDevice,
  registerSuperProperties,
  registerUserState,
  setPersonOnce,
} from "@/client/analytics/mixpanel";
import { type FirstRoundMe, useFirstRoundMe } from "@/client/services/pricing/first-round";
import { useAuthStore } from "@/client/stores/auth";
import { usePaymentStore } from "@/client/stores/payment";
import { isFirstRoundMarketingHost } from "@/client/utils/firstRoundAppUrl";

/** The plan and pass on every event from a signed-in user. */
export const planState = (me: FirstRoundMe | undefined) => {
  if (!me) return {};
  const plan = me.planKey ?? (me.planChosen ? "free" : "none");
  let passStatus = "none";
  if (me.pass) passStatus = me.pass.state === "active" ? "active" : "not_started";
  else if (me.lastPass) passStatus = "expired";
  return { plan, pass_status: passStatus };
};

/**
 * Starts analytics (Mixpanel + GA4 "First Round") and ties identity together:
 *   - first-touch attribution is a super property on every event and a set-once person property
 *     (Ga4ContextSync separately persists attribution in the backend AnalyticsContext),
 *   - links between firstround.com.au and theresume.io carry the visitor across (attribution.ts),
 *   - signed-in users are identified by internal user id only (email lives on the Mixpanel
 *     profile, never in event properties; GA4 only ever gets the id),
 *   - a device id handed over from the other site is merged into the user once,
 *   - every event says whether someone is signed in, and on which plan.
 */
export const MixpanelInitializer = () => {
  const user = useAuthStore((state) => state.user);
  const isPaid = usePaymentStore((state) => state.payment.isPaid);
  const { data: me } = useFirstRoundMe();

  useEffect(() => {
    initMixpanel();

    const touch = getFirstTouch();
    if (touch) registerSuperProperties(firstTouchProperties(touch));

    installHandoffDecorator(getDeviceId, handoffTarget(isFirstRoundMarketingHost()));
  }, []);

  useEffect(() => {
    registerUserState({ signed_in: Boolean(user?.id), ...(user?.id ? planState(me) : {}) });
  }, [user?.id, me]);

  useEffect(() => {
    ga4SetUser(user?.id ?? null);
    if (!user?.id) return;

    identifyUser(user, { is_paid: isPaid });

    const touch = getFirstTouch();
    if (touch) setPersonOnce(firstTouchProperties(touch));

    const device = getHandedOffDevice();
    const linkedKey = `first_touch_device_linked:${user.id}:${device}`;
    try {
      if (device && device !== getDeviceId() && !localStorage.getItem(linkedKey)) {
        linkDevice(device);
        localStorage.setItem(linkedKey, "1");
      }
    } catch {
      // Storage unavailable: skip the merge rather than repeat it every load.
    }
  }, [user, isPaid]);

  return null;
};
